import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Building2 } from "lucide-react";

export default function PlatformTenantsPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ slug: "", name: "", timezone: "UTC", owner_email: "", owner_full_name: "", owner_password: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/tenants", { params: { q } });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [q]);

  const create = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.owner_password) delete body.owner_password;
      await api.post("/platform/tenants", body);
      toast.success("Workspace created");
      setDialog(false);
      setForm({ slug: "", name: "", timezone: "UTC", owner_email: "", owner_full_name: "", owner_password: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="platform-tenants-page">
      <div className="flex items-center justify-between gap-2">
        <div className="relative w-96">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search workspaces…" className="pl-8"
            data-testid="platform-tenants-search"
          />
        </div>
        <Button onClick={() => setDialog(true)} data-testid="platform-create-tenant-btn">
          <Plus className="mr-1.5 h-4 w-4" /> New workspace
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workspace</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Last active</TableHead>
                <TableHead>Auth mode</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`platform-tenant-${t.slug}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="grid h-7 w-7 place-items-center rounded-md bg-secondary/10 text-secondary">
                        <Building2 className="h-3.5 w-3.5" />
                      </div>
                      {t.name}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{t.slug}</TableCell>
                  <TableCell>{t.user_count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {t.last_active_at ? new Date(t.last_active_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell><Badge variant="outline">{t.auth_mode || "native"}</Badge></TableCell>
                  <TableCell><Badge variant={t.status === "active" ? "default" : "outline"}>{t.status}</Badge></TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  No workspaces match.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg" data-testid="platform-create-tenant-dialog">
          <DialogHeader><DialogTitle>Create workspace</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="platform-tenant-slug" />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="platform-tenant-name" />
            </div>
            <div>
              <Label>Timezone</Label>
              <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} data-testid="platform-tenant-tz" />
            </div>
            <div>
              <Label>Owner email</Label>
              <Input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="platform-tenant-owner-email" />
            </div>
            <div className="col-span-2">
              <Label>Owner full name</Label>
              <Input value={form.owner_full_name} onChange={(e) => setForm({ ...form, owner_full_name: e.target.value })} data-testid="platform-tenant-owner-name" />
            </div>
            <div className="col-span-2">
              <Label>Owner password (optional — leave blank to send set-password link)</Label>
              <Input type="password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} data-testid="platform-tenant-owner-pw" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.slug || !form.name || !form.owner_email} data-testid="platform-create-tenant-submit">
              {saving ? "Creating…" : "Create workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
